/**
 * Activity → Compound Findings (Kill-Chain) - 2026-05-29
 *
 * Surfaces the compound-severity correlator (backend shipped 8a3ad1f).
 * When >=2 distinct DAM engines (UEBA / Sonar / Early-Warning) fire on
 * the same actor inside a rolling window, the correlator fuses them
 * into ONE compound finding with severity boosted a rung above the
 * max contributor - a cross-engine kill chain. This is the most
 * differentiated DAM capability and was backend-only until now.
 *
 * Backend: GET /v1/activity/compound-findings (+ /:id), POST /:id/acknowledge,
 * PATCH /:id/status (dismiss/escalate), POST /correlate (on-demand run).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  AlertTriangle,
  ArrowRight,
  Ban,
  CheckCircle2,
  Clock,
  Layers,
  Loader2,
  Play,
  RefreshCw,
  Radio,
  TrendingUp,
  Waves,
  XCircle,
  Zap,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listCompoundFindings,
  acknowledgeCompoundFinding,
  patchCompoundFindingStatus,
  runCompoundCorrelator,
  type CompoundFinding,
  type CompoundStatus,
} from '@/lib/api';

// ── Display maps ─────────────────────────────────────────────────

const SEVERITY_VARIANT: Record<string, 'critical' | 'high' | 'medium' | 'low' | 'glass'> = {
  critical: 'critical',
  high: 'high',
  medium: 'medium',
  low: 'low',
  info: 'glass',
};

const STATUS_VARIANT: Record<CompoundStatus, 'warning' | 'info' | 'critical' | 'glass'> = {
  open: 'warning',
  acknowledged: 'info',
  escalated: 'critical',
  dismissed: 'glass',
};

const ENGINE_META: Record<string, { label: string; icon: typeof Waves }> = {
  ueba: { label: 'UEBA', icon: TrendingUp },
  sonar: { label: 'Sonar', icon: Waves },
  early_warning: { label: 'Early Warning', icon: Radio },
};

function engineLabel(key: string): string {
  return ENGINE_META[key]?.label ?? key;
}

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function ago(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0 || Number.isNaN(ms)) return '-';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Page ─────────────────────────────────────────────────────────

export function CompoundFindings(): JSX.Element {
  const [findings, setFindings] = useState<CompoundFinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<CompoundStatus | ''>('');
  const [actionPending, setActionPending] = useState<string | null>(null);
  const [correlating, setCorrelating] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await listCompoundFindings({
        ...(statusFilter ? { status: statusFilter } : {}),
        limit: 200,
      });
      setFindings(r.findings);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load compound findings');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const counts = useMemo(() => {
    const c = { open: 0, acknowledged: 0, escalated: 0, dismissed: 0 };
    for (const f of findings) c[f.status] += 1;
    return c;
  }, [findings]);

  async function handleCorrelate(): Promise<void> {
    setCorrelating(true);
    setBanner(null);
    setError(null);
    try {
      const r = await runCompoundCorrelator({ lookbackHours: 168, windowHours: 12 });
      setBanner(
        `Correlation run complete - fetched ${r.contributingFetched.ueba} UEBA / ${r.contributingFetched.sonar} Sonar / ${r.contributingFetched.earlyWarning} Early-Warning findings · ${r.draftsProduced} kill-chain drafts · ${r.newCompoundFindings} new compound findings · ${r.durationMs}ms`,
      );
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'correlation run failed');
    } finally {
      setCorrelating(false);
    }
  }

  async function handleAck(f: CompoundFinding): Promise<void> {
    setActionPending(f.id);
    try {
      await acknowledgeCompoundFinding(f.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'acknowledge failed');
    } finally {
      setActionPending(null);
    }
  }

  async function handleStatus(f: CompoundFinding, status: 'dismissed' | 'escalated'): Promise<void> {
    const reason = window.prompt(
      `${status === 'escalated' ? 'Escalate' : 'Dismiss'} this kill-chain finding - reason (min 3 chars):`,
    );
    if (!reason || reason.trim().length < 3) return;
    setActionPending(f.id);
    try {
      await patchCompoundFindingStatus(f.id, status, reason.trim());
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'status change failed');
    } finally {
      setActionPending(null);
    }
  }

  if (loading && findings.length === 0) {
    return (
      <PageContainer>
        <PageHeader title="Kill-Chain Findings" subtitle="Cross-engine compound correlation" />
        <LoadingState caption="Loading compound findings…" />
      </PageContainer>
    );
  }

  if (error && findings.length === 0) {
    return (
      <PageContainer>
        <PageHeader title="Kill-Chain Findings" subtitle="Cross-engine compound correlation" />
        <ErrorState
          title="Couldn't load compound findings"
          detail={error}
          onRetry={refresh}
          severity="critical"
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Kill-Chain Findings"
        subtitle="Cross-engine compound correlation. When ≥2 DAM engines (UEBA · Sonar · Early-Warning) fire on the same actor inside a rolling window, they fuse into one kill-chain finding with severity boosted a rung above the strongest signal."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={handleCorrelate} disabled={correlating}>
              {correlating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Correlating…
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Run correlation
                </>
              )}
            </Button>
          </div>
        }
      />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Open" value={counts.open} icon={AlertTriangle} glow={counts.open > 0 ? 'high' : 'low'} />
        <KpiCard label="Acknowledged" value={counts.acknowledged} icon={CheckCircle2} glow="low" />
        <KpiCard label="Escalated" value={counts.escalated} icon={Zap} glow={counts.escalated > 0 ? 'critical' : 'low'} />
        <KpiCard label="Dismissed" value={counts.dismissed} icon={Ban} />
      </div>

      {banner ? (
        <GlassCard className="mb-4 border-success/40">
          <div className="p-4 flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4 text-success" />
            <span>{banner}</span>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setBanner(null)}>
              Dismiss
            </Button>
          </div>
        </GlassCard>
      ) : null}

      {error && findings.length > 0 ? (
        <GlassCard className="mb-4 border-destructive/30">
          <div className="p-4 flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span>{error}</span>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setError(null)}>
              Dismiss
            </Button>
          </div>
        </GlassCard>
      ) : null}

      {/* Filter */}
      <div className="mb-4 flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Status</span>
        <select
          className="rounded-md border bg-background px-3 py-1.5 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as CompoundStatus | '')}
        >
          <option value="">All</option>
          <option value="open">Open</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="escalated">Escalated</option>
          <option value="dismissed">Dismissed</option>
        </select>
      </div>

      {/* Kill-chain list */}
      {findings.length === 0 ? (
        <GlassCard>
          <div className="p-12 text-center text-muted-foreground">
            <Layers className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <div className="text-sm">
              No compound findings{statusFilter ? ` with status "${statusFilter}"` : ''} yet.
            </div>
            <div className="text-xs mt-1">
              Run correlation to fuse recent single-engine findings into kill chains.
            </div>
          </div>
        </GlassCard>
      ) : (
        <div className="space-y-4">
          {findings.map((f) => {
            const isBusy = actionPending === f.id;
            const engines = f.enginesFired.split(',').filter(Boolean);
            const sorted = [...f.contributingFindings].sort(
              (a, b) => new Date(a.detectedAt).getTime() - new Date(b.detectedAt).getTime(),
            );
            return (
              <motion.div
                key={f.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <GlassCard className={f.severity === 'critical' ? 'border-sev-critical/40' : ''}>
                  {/* Header row */}
                  <div className="p-5 flex items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={SEVERITY_VARIANT[f.severity] ?? 'glass'} className="gap-1">
                          <Zap className="h-3 w-3" />
                          {f.severity}
                        </Badge>
                        {f.boostApplied ? (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            {f.baseSeverity}
                            <ArrowRight className="h-3 w-3" />
                            <span className="font-medium text-foreground">{f.severity}</span>
                            <span className="text-muted-foreground">(boosted)</span>
                          </span>
                        ) : null}
                        <Badge variant={STATUS_VARIANT[f.status]}>{f.status}</Badge>
                      </div>
                      <div className="mt-2 font-medium">
                        Actor: <span className="font-mono text-sm">{f.actorUserId}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                        <Layers className="h-3.5 w-3.5" />
                        {f.engineCount} engines fired:
                        {engines.map((e) => {
                          const meta = ENGINE_META[e];
                          const Icon = meta?.icon ?? Layers;
                          return (
                            <Badge key={e} variant="info" className="gap-1">
                              <Icon className="h-3 w-3" />
                              {engineLabel(e)}
                            </Badge>
                          );
                        })}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        window {fmtTime(f.firstSignalAt)} → {fmtTime(f.lastSignalAt)} · detected{' '}
                        {ago(f.detectedAt)}
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="flex gap-2">
                      {f.status === 'open' ? (
                        <Button variant="outline" size="sm" disabled={isBusy} onClick={() => handleAck(f)}>
                          {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                          Acknowledge
                        </Button>
                      ) : null}
                      {f.status !== 'escalated' && f.status !== 'dismissed' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isBusy}
                          onClick={() => handleStatus(f, 'escalated')}
                          className="text-sev-critical"
                        >
                          <Zap className="h-4 w-4 mr-1" />
                          Escalate
                        </Button>
                      ) : null}
                      {f.status !== 'dismissed' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={isBusy}
                          onClick={() => handleStatus(f, 'dismissed')}
                          className="text-muted-foreground"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Dismiss
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {/* Kill-chain timeline of contributing findings */}
                  <div className="px-5 pb-5">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                      Kill chain ({sorted.length} signals)
                    </div>
                    <ol className="relative border-l border-border ml-2 space-y-3">
                      {sorted.map((c, i) => {
                        const meta = ENGINE_META[c.engine];
                        const Icon = meta?.icon ?? Layers;
                        return (
                          <li key={`${c.findingId}-${i}`} className="ml-4">
                            <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-primary" />
                            <div className="flex items-center gap-2 flex-wrap text-sm">
                              <Badge variant="info" className="gap-1">
                                <Icon className="h-3 w-3" />
                                {engineLabel(c.engine)}
                              </Badge>
                              <Badge variant={SEVERITY_VARIANT[c.severity] ?? 'glass'}>
                                {c.severity}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {fmtTime(c.detectedAt)}
                              </span>
                            </div>
                            <div className="text-sm mt-1">{c.summary}</div>
                          </li>
                        );
                      })}
                    </ol>
                    {f.resolutionReason ? (
                      <div className="mt-3 text-xs text-muted-foreground">
                        Resolution: {f.resolutionReason}
                      </div>
                    ) : null}
                  </div>
                </GlassCard>
              </motion.div>
            );
          })}
        </div>
      )}
    </PageContainer>
  );
}

export default CompoundFindings;
